import {
  booleanAttribute,
  Component,
  input,
  Input,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { QuestionKind } from '@core/interview-templates-types';
import type { RadioGroupItem, SelectOption } from '@core/user-interface-legacy';
import {
  CheckboxComponent,
  GroupComponent,
  ProtectedControlDirective,
  RadioFormGroupComponent,
  SelectComponent,
} from '@core/user-interface-legacy';

import { InterviewBaseBoxComponent } from '../interview-base-box/interview-base-box.component';
import type { QuestionBase } from '../interview-kind';

type QuestionChoiceKinds =
  | QuestionKind.Dropdown
  | QuestionKind.SingleChoice
  | QuestionKind.MultipleChoice
  | QuestionKind.Boolean;

type QuestionOptionsControl = FormControl<{ title: string }>;

interface QuestionSingleChoiceControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.SingleChoice>;
  content: FormControl<string>;
  options?: FormArray<QuestionOptionsControl>;
}
export type QuestionSingleChoiceControl = FormGroup<QuestionSingleChoiceControls>;

interface QuestionMultipleChoiceControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.MultipleChoice>;
  contents: FormArray<FormControl<string>>;
  options: FormArray<QuestionOptionsControl>;
}
type QuestionMultipleChoiceControl = FormGroup<QuestionMultipleChoiceControls>;

interface QuestionDropdownControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.Dropdown>;
  content: FormControl<string>;
  options: FormArray<QuestionOptionsControl>;
}
type QuestionDropdownControl = FormGroup<QuestionDropdownControls>;

interface QuestionBooleanControls extends QuestionBase {
  questionKind: FormControl<QuestionKind.Boolean>;
  content: FormControl<string>;
  options: FormArray<QuestionOptionsControl>;
}
type QuestionBooleanControl = FormGroup<QuestionBooleanControls>;

type QuestionChoiceControl =
  | QuestionSingleChoiceControl
  | QuestionMultipleChoiceControl
  | QuestionDropdownControl
  | QuestionBooleanControl;

type IsControl<T extends QuestionChoiceControl, Part extends Record<string, AbstractControl>> =
  T extends FormGroup<infer R> ? (R extends Part ? FormGroup<R> : never) : never;

@Component({
  selector: 'rt-interview-choice',
  templateUrl: 'interview-choice.component.html',
  styleUrls: ['./interview-choice.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    RadioFormGroupComponent,
    ProtectedControlDirective,
    GroupComponent,
    CheckboxComponent,
    SelectComponent,
    InterviewBaseBoxComponent,
  ],
})
export class InterviewChoiceComponent<Control extends QuestionChoiceControl> {
  private readonly i18n = inject(I18nService);

  public readonly formSubmissionAttempt = input<boolean>(false);
  public readonly customOptions = input<SelectOption[] | RadioGroupItem[]>();
  public readonly errorMessage = input<string | null>(undefined!);
  public readonly hasProtectedControls = input.required<boolean, boolean | string>({
    transform: booleanAttribute,
  });
  @Input() public questionForm: Control;

  public options: SelectOption[] | RadioGroupItem[] = [];

  public QuestionKind: typeof QuestionKind = QuestionKind;

  public ngOnChanges(changes: SimpleChanges): void {
    const customOptions = this.customOptions();
    if (changes.customOptions && customOptions) {
      this.options = customOptions;
    }

    if (!customOptions) {
      const value = (this.questionForm && this.questionForm.getRawValue()) || {};
      if (QuestionKind.Dropdown === value.questionKind) {
        this.options = this.prepareSelectItems(value.options);
      } else if (QuestionKind.SingleChoice === value.questionKind && value.options) {
        this.options = this.prepareRadioItems(value.options);
      } else if (QuestionKind.Boolean === value.questionKind) {
        this.options = this.getBooleanOptionList();
      }
    }
  }

  public isInvalid(questionForm: Control): boolean {
    const isContentInvalid = this.isContentControl(questionForm)
      ? questionForm.controls.content.invalid
      : false;
    const isContentsInvalid = this.isContentsControl(questionForm)
      ? questionForm.controls.contents.invalid
      : false;
    const isDirtyOrTouched = questionForm.dirty || questionForm.touched;

    return (isContentInvalid || isContentsInvalid) && isDirtyOrTouched;
  }

  public trackByOption<T extends QuestionOptionsControl>(index: number, option: T): string {
    return option.value.title;
  }

  public isQuestionType<T extends Control, K extends QuestionChoiceKinds>(
    questionForm: T,
    kind: K,
  ): questionForm is IsControl<T, { questionKind: FormControl<K> }> {
    return questionForm.getRawValue().questionKind === kind;
  }

  private getBooleanOptionList(): RadioGroupItem[] {
    return [
      {
        option: this.i18n.translate('app.interview.boolean.yes'),
        value: 'yes',
      },
      {
        option: this.i18n.translate('app.interview.boolean.no'),
        value: 'no',
      },
    ];
  }

  private prepareSelectItems(options: Array<{ title: string }>): SelectOption[] {
    return options.map(option => ({
      label: option.title,
      value: option.title,
    }));
  }

  private prepareRadioItems(options: Array<{ title: string }>): RadioGroupItem[] {
    return options.map(option => ({
      option: option.title,
      value: option.title,
    }));
  }

  private isContentControl<T extends Control>(
    control: T,
  ): control is IsControl<T, { content: FormControl<string> }> {
    return 'content' in control.controls;
  }

  private isContentsControl<T extends Control>(
    control: T,
  ): control is IsControl<T, { contents: FormArray<FormControl<string>> }> {
    return 'contents' in control.controls;
  }
}
